import React from 'react';
import { X, Video, Trash2, Repeat } from 'lucide-react';
import { COR, FONTE } from './ui';

// Dias da semana no valor do Graph (o backend usa direto) + rótulo PT-BR.
const DIAS_SEMANA = [
    { v: 'sunday', l: 'Domingo' }, { v: 'monday', l: 'Segunda' }, { v: 'tuesday', l: 'Terça' },
    { v: 'wednesday', l: 'Quarta' }, { v: 'thursday', l: 'Quinta' }, { v: 'friday', l: 'Sexta' },
    { v: 'saturday', l: 'Sábado' },
];
// Dia da semana (valor do Graph) de um 'YYYY-MM-DDTHH:mm' — usado como default do
// seletor quando a recorrência é semanal e a pessoa ainda não escolheu o dia.
const diaSemanaDe = (inicio) => {
    if (!inicio) return 'monday';
    const [data] = inicio.split('T');
    const [a, m, d] = data.split('-').map(Number);
    return DIAS_SEMANA[new Date(a, m - 1, d).getDay()].v;
};

// =====================================================================
// FORMULÁRIO DE COMPROMISSO (Teams/M365) — modal de criar/editar
// =====================================================================
// Estava embutido na AgendaCorporativa; saiu de lá quando o grid de mês
// (CalendarioAgenda) passou a precisar do MESMO formulário. Os dois montam este
// modal e mandam o resultado para o `salvar` do useAgendaMs.
//
// zIndex 1000: precisa ficar ACIMA do modal da agenda (950, no BotaoAgenda) —
// senão o formulário nasce atrás do calendário que o abriu.
//
// Props:
//   - form      : { id?, assunto, inicio, fim, local, participantes, online }
//   - setForm   : setter do form (edição controlada pelo pai)
//   - erroMsg   : mensagem de validação/erro vinda do useAgendaMs
//   - onSalvar  : dispara o salvamento
//   - onFechar  : fecha sem salvar
//   - onExcluir : opcional. Só aparece na EDIÇÃO. Existe para o grid de mês, onde
//                 a tag do dia abre direto a edição e não há outro caminho para
//                 excluir (no card da Home, a lixeira já fica na linha da lista).

function FormCompromisso({ form, setForm, erroMsg, onSalvar, onFechar, onExcluir }) {
    const input = {
        width: '100%', boxSizing: 'border-box', padding: '9px', marginTop: '4px',
        border: `1px solid ${COR.bordaSuave}`, borderRadius: '10px', fontFamily: FONTE,
    };
    const rotulo = { fontSize: '12px', color: '#666' };

    // Recorrência: só a CRIAÇÃO oferece (série nova). Ao editar uma série já
    // existente, não mexemos em data/hora nem no padrão — mudar a data do mestre
    // deslocaria a série inteira. Aí travamos os campos de data e explicamos o
    // caminho (excluir + recriar).
    const rec = form.recorrencia || { tipo: 'nenhuma', diaSemana: '', ate: '' };
    const setRec = (patch) => setForm({ ...form, recorrencia: { ...rec, ...patch } });
    const podeRecorrer = !form.id;              // criação
    const editandoSerie = !!form.id && !!form.serie;

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}
        >
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '22px', width: '100%', maxWidth: '420px', maxHeight: '90vh', overflowY: 'auto', fontFamily: FONTE }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', color: COR.texto }}>{form.id ? 'Editar compromisso' : 'Novo compromisso'}</h3>
                    <button onClick={onFechar} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px', color: '#999', display: 'flex' }}><X size={18} /></button>
                </div>

                <label style={rotulo}>Título</label>
                <input
                    value={form.assunto}
                    onChange={(e) => setForm({ ...form, assunto: e.target.value })}
                    style={{ ...input, marginBottom: '12px' }}
                />

                {editandoSerie && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 12px', padding: '9px 11px', backgroundColor: '#f4f0fb', border: '1px solid #e3d9f5', borderRadius: '10px', fontSize: '12px', color: '#5b4a86', lineHeight: 1.5 }}>
                        <Repeat size={15} style={{ flexShrink: 0 }} />
                        <span>Compromisso recorrente — as alterações valem para <strong>toda a série</strong>. Para mudar o horário ou a repetição, exclua e crie de novo.</span>
                    </div>
                )}

                <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ flex: 1 }}>
                        <label style={rotulo}>Início</label>
                        <input type="datetime-local" value={form.inicio} disabled={editandoSerie} onChange={(e) => setForm({ ...form, inicio: e.target.value })} style={{ ...input, ...(editandoSerie ? { backgroundColor: '#f3f4f6', color: '#9ca3af' } : {}) }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={rotulo}>Fim</label>
                        <input type="datetime-local" value={form.fim} disabled={editandoSerie} onChange={(e) => setForm({ ...form, fim: e.target.value })} style={{ ...input, ...(editandoSerie ? { backgroundColor: '#f3f4f6', color: '#9ca3af' } : {}) }} />
                    </div>
                </div>

                {/* Repetir — só na criação (série nova). O Graph resolve a recorrência;
                    a leitura já vem com as ocorrências expandidas. */}
                {podeRecorrer && (
                    <div style={{ marginTop: '12px' }}>
                        <label style={{ ...rotulo, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Repeat size={13} /> Repetir
                        </label>
                        <select value={rec.tipo} onChange={(e) => setRec({ tipo: e.target.value })} style={{ ...input, marginTop: '4px' }}>
                            <option value="nenhuma">Não repete</option>
                            <option value="diaria">Diariamente</option>
                            <option value="semanal">Semanalmente</option>
                            <option value="mensal">Mensalmente</option>
                        </select>

                        {rec.tipo !== 'nenhuma' && (
                            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                                {rec.tipo === 'semanal' && (
                                    <div style={{ flex: 1 }}>
                                        <label style={rotulo}>No dia</label>
                                        <select
                                            value={rec.diaSemana || diaSemanaDe(form.inicio)}
                                            onChange={(e) => setRec({ diaSemana: e.target.value })}
                                            style={input}
                                        >
                                            {DIAS_SEMANA.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                                        </select>
                                    </div>
                                )}
                                <div style={{ flex: 1 }}>
                                    <label style={rotulo}>Repetir até (opcional)</label>
                                    <input type="date" value={rec.ate || ''} onChange={(e) => setRec({ ate: e.target.value })} style={input} />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <label style={{ ...rotulo, display: 'block', marginTop: '12px' }}>Local (opcional)</label>
                <input value={form.local} onChange={(e) => setForm({ ...form, local: e.target.value })} style={input} />

                <label style={{ ...rotulo, display: 'block', marginTop: '12px' }}>
                    Participantes (e-mails separados por vírgula)
                </label>
                <input
                    value={form.participantes}
                    onChange={(e) => setForm({ ...form, participantes: e.target.value })}
                    placeholder="fulano@mcdes.com.br, ciclano@mcdes.com.br"
                    style={input}
                />
                {/* Diz o que vai acontecer ANTES de acontecer: cada participante
                    recebe o convite do Outlook (da Microsoft) E o e-mail do portal.
                    São dois e-mails — decisão consciente de 2026-07-16, ver o PLANO.
                    Sem este aviso, quem cria não faz ideia de quanta caixa de
                    entrada está movimentando. */}
                {(form.participantes || '').includes('@') && (
                    <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#999', lineHeight: 1.5 }}>
                        Cada participante recebe o convite do Outlook e um e-mail do portal.
                    </p>
                )}

                {/* Marcar = vira reunião do Teams e o Graph gera o link de ingresso. */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '13px', color: COR.texto, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!form.online} onChange={(e) => setForm({ ...form, online: e.target.checked })} />
                    <Video size={15} color={COR.vermelhoEscuro} /> Reunião online do Teams (gera link)
                </label>

                {erroMsg && <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '10px' }}>{erroMsg}</p>}

                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginTop: '18px' }}>
                    {/* Excluir fica à ESQUERDA, longe do Salvar: é destrutivo e não
                        deve ficar vizinho do botão de confirmação. */}
                    {form.id && onExcluir && (
                        <button
                            onClick={onExcluir}
                            style={{ marginRight: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px', border: '1px solid #fecaca', borderRadius: '10px', padding: '9px 14px', backgroundColor: '#fff', color: '#dc2626', fontWeight: 600, cursor: 'pointer', fontFamily: FONTE, fontSize: '13px' }}
                        >
                            <Trash2 size={15} /> Excluir
                        </button>
                    )}
                    <button onClick={onFechar} style={{ border: `1px solid ${COR.bordaSuave}`, borderRadius: '10px', padding: '9px 16px', backgroundColor: '#fff', color: COR.texto, cursor: 'pointer', fontFamily: FONTE }}>Cancelar</button>
                    <button onClick={onSalvar} style={{ border: 'none', borderRadius: '10px', padding: '9px 18px', backgroundColor: COR.vermelhoEscuro, color: '#fff', fontWeight: 600, cursor: 'pointer', fontFamily: FONTE }}>Salvar</button>
                </div>
            </div>
        </div>
    );
}

export default FormCompromisso;
